import { useQuery } from '@tanstack/react-query';
import { catalogApi } from '../../api/endpoints/catalog';
import { applicationApi } from '../../api/endpoints/application';
import type { Permission, Scope } from '../../api/types/permissions';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Banner } from '../ui/Banner';
import { Loading } from '@/kit';
import { groupByAction } from '../../lib/permissionOrder';

/**
 * Item ya normalizado al shape que necesita el render del picker. Tanto el catálogo GLOBAL
 * (`PermissionCatalogItem`) como el catálogo de una APP (`ApplicationPermissionLocal`) se
 * proyectan a esta forma para reusar el mismo agrupamiento/orden y el mismo checkbox.
 */
interface PickerItem {
    key: Permission;
    category: string;
    displayName: string;
    description: string;
}

interface Props {
    value: Permission[];
    onChange: (next: Permission[]) => void;
    /**
     * Filtra qué permisos del catálogo GLOBAL ofrecer por su scope (solo aplica si NO viene
     * `applicationId`):
     * - sin valor (default): roles de TENANT hacia abajo → se EXCLUYEN los `PLATFORM` (comportamiento
     *   histórico del flujo tenant; no romper sus tests/forms).
     * - `'PLATFORM'`: roles PLATFORM → se ofrecen SOLO los permisos `platform.*` (scope PLATFORM).
     */
    scopeFilter?: Scope;
    /**
     * Si viene (y no vacío), el picker se alimenta del catálogo de permisos de ESA aplicación
     * (`applicationApi.listPermissions`) en vez del catálogo global. Es lo que corresponde a los
     * editores de rol de un TENANT: el usuario ve "la lista de permisos de la app" del tenant.
     * Si NO viene (o viene vacío), comportamiento histórico EXACTO (catálogo global + `scopeFilter`).
     */
    applicationId?: string;
}

export function PermissionPicker({ value, onChange, scopeFilter, applicationId }: Props) {
    const useAppCatalog = !!applicationId;

    // Un solo hook por rama, gateado por `enabled` para respetar las reglas de hooks sin fetchear
    // el catálogo que no corresponde.
    const globalQuery = useQuery({
        queryKey: ['catalog', 'permissions'],
        queryFn: catalogApi.listPermissions,
        staleTime: 5 * 60_000,
        enabled: !useAppCatalog,
    });
    const appQuery = useQuery({
        queryKey: ['application', applicationId, 'permissions'],
        queryFn: () => applicationApi.listPermissions(applicationId as string),
        staleTime: 5 * 60_000,
        enabled: useAppCatalog,
    });

    const isLoading = useAppCatalog ? appQuery.isLoading : globalQuery.isLoading;
    if (isLoading) return <Loading />;

    // Si el catálogo (el de la rama activa) falla, un early-return con estado de error EXPLÍCITO —el
    // mismo Banner de error que ya usan las pantallas de aplicaciones—. Sin esto, `data` queda undefined,
    // `items` colapsa a [] y el picker se renderizaría VACÍO en silencio (invitando a guardar un rol
    // despermisado); peor en la rama por-app, donde `catalogKeys` vacío reclasificaría TODOS los permisos
    // actuales del rol como "fuera del catálogo" (falsa pérdida de datos). Mejor: avisar y que reintente.
    const isError = useAppCatalog ? appQuery.isError : globalQuery.isError;
    if (isError) {
        return (
            <Banner variant="error" title="No se pudo cargar el catálogo de permisos">
                Intenta de nuevo.
            </Banner>
        );
    }

    // Proyecta cada catálogo al shape común del render.
    const items: PickerItem[] = useAppCatalog
        ? (appQuery.data?.items ?? []).map((p) => ({
              key: p.permissionKey as Permission,
              category: p.category ?? 'general',
              displayName: p.displayName,
              description: p.description,
          }))
        : (globalQuery.data?.items ?? [])
              .filter((p) => (scopeFilter ? p.scope === scopeFilter : p.scope !== 'PLATFORM'))
              .filter((p) => !p.isSystemOnly)
              .map((p) => ({
                  key: p.key,
                  category: p.category,
                  displayName: p.displayName,
                  description: p.description,
              }));

    // Permisos que el rol YA tiene pero que NO están en el catálogo ofrecido (legacy / de otra app).
    // Los mostramos aparte y marcados para NO perderlos silenciosamente al guardar. Solo aplica en la
    // rama por-app (en la global el picker histórico no los contemplaba y no queremos cambiar su UX).
    const catalogKeys = new Set(items.map((p) => p.key));
    const orphanKeys: Permission[] = useAppCatalog ? value.filter((k) => !catalogKeys.has(k)) : [];

    const grouped = items.reduce<Record<string, PickerItem[]>>((acc, p) => {
        (acc[p.category] ??= []).push(p);
        return acc;
    }, {});

    const toggle = (key: Permission): void => {
        onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
    };

    return (
        <div className="space-y-4">
            {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([category, perms]) => (
                    <section key={category} className="space-y-3">
                        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                            {category}
                        </h3>
                        {groupByAction(perms).map(({ group, items: groupItems }) => (
                            <div key={group.id} className="space-y-2 pl-3 border-l">
                                <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground/80">
                                    {group.label}
                                </h4>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {groupItems.map((p) => (
                                        <Label
                                            key={p.key}
                                            className="flex items-start gap-2 cursor-pointer"
                                        >
                                            <Checkbox
                                                checked={value.includes(p.key)}
                                                onCheckedChange={() => {
                                                    toggle(p.key);
                                                }}
                                                className="mt-0.5"
                                            />
                                            <span className="flex flex-col">
                                                <span className="text-sm font-medium leading-tight">
                                                    {p.displayName || p.key}
                                                </span>
                                                <span className="text-xs text-muted-foreground leading-tight">
                                                    {p.description}
                                                </span>
                                            </span>
                                        </Label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ))}

            {orphanKeys.length > 0 && (
                <section className="space-y-3">
                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                        Permisos actuales fuera del catálogo de la app
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        Este rol tiene permisos que ya no forman parte del catálogo de la
                        aplicación. Se conservan al guardar salvo que los desmarques.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {[...orphanKeys]
                            .sort((a, b) => a.localeCompare(b))
                            .map((key) => (
                                <Label key={key} className="flex items-start gap-2 cursor-pointer">
                                    <Checkbox
                                        checked={value.includes(key)}
                                        onCheckedChange={() => {
                                            toggle(key);
                                        }}
                                        className="mt-0.5"
                                    />
                                    <span className="text-sm font-mono leading-tight">{key}</span>
                                </Label>
                            ))}
                    </div>
                </section>
            )}
        </div>
    );
}
